import {TilesState} from '../state/tiles.state';
import {
  Actions,
  TILE_LINKED_TO_APPS,
  TILE_LOADED,
  TILE_UNLINKED_FROM_APPS,
  TILES_FOR_APP_LOADED,
  TILES_FOR_USER_LOADED,
  TILES_LOADED,
  TILES_TILE_DELETED,
  TILES_TILE_SAVED,
} from '../actions/tiles.actions';
import {Tile, TileId} from '../../shared/models/tile';
import {AppId} from '../../shared/models/app';

const initialState: TilesState = {
  all: {},
  byApp: {},
};

export function TilesReducer(state: TilesState = initialState, action: Actions): TilesState {
  let newAll: { [tID: TileId]: Tile } = {...state.all};
  let newByApp: { [aID: AppId]: { [tID: TileId]: Tile } } = {};

  for (let appId in state.byApp) {
    newByApp[appId] = {...state.byApp[appId]};
  }

  switch (action.type) {
    case TILE_LOADED:
      newAll[action.tile.ID] = Tile.create(action.tile);

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILES_LOADED:
      for (let t of action.tiles) {
        const existing = newAll[t.ID];
        if (existing?.Tags?.length && !t.Tags) {
          const merged = Tile.create(t);
          merged.Tags = [...existing.Tags];
          newAll[t.ID] = merged;
        } else {
          newAll[t.ID] = t;
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILES_FOR_USER_LOADED:
      newAll = {};

      const loadedTiles = action.tiles;

      loadedTiles.map((newTile) => {
        newAll[newTile.ID] = Tile.create(newTile);
      });

      // Update the tile in "byApp"
      for (let appId in newByApp) {
        const appTiles = newByApp[appId];

        for (let t of loadedTiles) {
          const tileId = t.ID;

          // Ensure `byApp` and `all` are using same object refs
          if (appTiles.hasOwnProperty(tileId)) {
            appTiles[tileId] = newAll[tileId];
          }

          // Remove tiles from this `byApp` if tile not found in user's `all` list
          if (!newAll.hasOwnProperty(tileId)) {
            delete appTiles[tileId];
          }
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILES_FOR_APP_LOADED:
      // Also, ensure that all tiles loaded for the app are stored in a fresh byApp
      // array for THIS app only; we refresh it from scratch in case tiles have been
      // deleted in the API
      newByApp[action.appId] = {};

      for (let tile of action.tiles) {
        newAll[tile.ID] = tile;
        newByApp[action.appId][tile.ID] = tile;
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILES_TILE_SAVED:
      const savedTile = Tile.create(action.tile);

      // Update the tile in "all"
      newAll[action.tile.ID] = savedTile;

      // Update the tile in "byApp"
      for (let appId in newByApp) {
        const appTiles = newByApp[appId];

        if (appTiles.hasOwnProperty(savedTile.ID)) {
          appTiles[savedTile.ID] = savedTile;
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILES_TILE_DELETED:
      for (let tileId in newAll) {
        if (newAll[tileId].ID === action.tileId) {
          delete newAll[tileId];
        }
      }

      for (let appId in newByApp) {
        const appTiles = newByApp[appId];

        for (let tileId in appTiles) {
          if (appTiles[tileId].ID === action.tileId) {
            delete appTiles[tileId];
          }
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILE_UNLINKED_FROM_APPS:
      for (let appId of action.appIds) {
        if (newByApp[appId]) {
          delete newByApp[appId][action.tileId];
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };
    case TILE_LINKED_TO_APPS:
      for (let appId of action.appIds) {
        if (newByApp[appId]) {
          newByApp[appId][action.tileId] = newAll[action.tileId];
        }
      }

      return {
        ...state,
        all: newAll,
        byApp: newByApp,
      };

    default:
      return state;
  }
}
